import { clamp, preventDefault, isMobileBreakpoint } from '@theme/utilities';
import { ZoomDialog } from '@theme/zoom-dialog';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;
const WHEEL_ZOOM_STEP = 0.15;

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = MIN_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = MIN_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #touchInitialized = false;
  /** @type {boolean} */
  #desktopInitialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  get #image() {
    return this.querySelector('img');
  }

  connectedCallback() {
    if (!this.#image) return;

    this.#initResizeListener();
    this.#setupDialogCloseListener();

    // Start at 1× so dialog scroll navigates the vertical stack; pinch/double-tap zooms.
    this.#scale = MIN_ZOOM;
    this.#startScale = MIN_ZOOM;

    if (isMobileBreakpoint()) {
      this.#initTouchListeners();
    } else {
      this.#initDesktopListeners();
    }

    this.#updateTransform();
  }

  /**
   * Resets zoom for the current breakpoint (called from zoom-dialog).
   */
  resetZoomState() {
    this.#hasManualZoom = false;
    this.#applyZoomState(MIN_ZOOM, 0, 0);
    this.#resetGestureState();
    this.#requestUpdateTransform();

    if (!isMobileBreakpoint()) {
      this.#scheduleDesktopLayoutSync();
    }
  }

  /**
   * Re-measures after the lightbox layout is painted (desktop).
   */
  #scheduleDesktopLayoutSync() {
    const sync = () => this.#requestUpdateTransform();

    requestAnimationFrame(() => {
      sync();
      requestAnimationFrame(sync);
    });

    if (this.#image && !this.#image.complete) {
      this.#image.addEventListener('load', sync, { once: true });
    }
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  /**
   * Override parent zoom dialog's close method to include reset functionality
   */
  #setupDialogCloseListener() {
    const zoomDialog = /** @type {ZoomDialog} */ (this.closest('zoom-dialog'));
    if (!zoomDialog || typeof zoomDialog.close !== 'function') return;

    if (zoomDialog.dataset.dragZoomCloseHook === 'true') return;
    zoomDialog.dataset.dragZoomCloseHook = 'true';

    const originalClose = zoomDialog.close.bind(zoomDialog);

    zoomDialog.close = async (...args) => {
      zoomDialog.querySelectorAll('drag-zoom-wrapper').forEach((wrapper) => {
        if (wrapper instanceof DragZoomWrapper) {
          wrapper.resetZoomState();
        }
      });

      return await originalClose(...args);
    };
  }

  #initTouchListeners() {
    if (this.#touchInitialized) return;
    this.#touchInitialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    this.#updateTransform();
  }

  #initDesktopListeners() {
    if (this.#desktopInitialized) return;
    this.#desktopInitialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('wheel', this.#handleWheel, options);
    this.addEventListener('dblclick', this.#handleDoubleClick, options);
    this.addEventListener('mousedown', this.#handleMouseDown, options);

    this.#updateTransform();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
    window.removeEventListener('mousemove', this.#handleMouseMove);
    window.removeEventListener('mouseup', this.#handleMouseUp);
  }

  #handleResize = () => {
    if (!this.#touchInitialized && isMobileBreakpoint()) {
      this.#initTouchListeners();
    }

    if (!this.#desktopInitialized && !isMobileBreakpoint()) {
      this.#initDesktopListeners();
    }

    if (this.#touchInitialized || this.#desktopInitialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    // Vertical gallery: let the dialog scroll by default.
    // Zoom only on pinch (ctrl/meta+wheel) or when already zoomed.
    const wantsZoom = event.ctrlKey || event.metaKey || this.#scale > MIN_ZOOM + 0.01;
    if (!wantsZoom) return;

    preventDefault(event);

    const delta = event.deltaY < 0 ? WHEEL_ZOOM_STEP : -WHEEL_ZOOM_STEP;
    const oldScale = this.#scale;
    const newScale = clamp(this.#scale + delta, MIN_ZOOM, MAX_ZOOM);

    if (newScale === oldScale) return;

    this.#hasManualZoom = true;
    this.#scale = newScale;
    this.#adjustTranslationForZoomAtPoint(oldScale, event.clientX, event.clientY);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate = { x: 0, y: 0 };
      this.#hasManualZoom = false;
    }

    this.#requestUpdateTransform();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleDoubleClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    this.#handleZoomToggleAtPoint(event.clientX, event.clientY);
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseDown = (event) => {
    if (event.button !== 0 || this.#scale <= MIN_ZOOM) return;

    preventDefault(event);
    event.stopPropagation();

    this.#startPosition = { x: event.clientX, y: event.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;

    window.addEventListener('mousemove', this.#handleMouseMove, { passive: false });
    window.addEventListener('mouseup', this.#handleMouseUp, { passive: true });
  };

  /**
   * @param {MouseEvent} event
   */
  #handleMouseMove = (event) => {
    if (!this.#isDragging) return;

    preventDefault(event);
    this.#processDragGesture(event);
  };

  #handleMouseUp = () => {
    this.#isDragging = false;
    this.#hasDraggedBeyondThreshold = false;
    window.removeEventListener('mousemove', this.#handleMouseMove);
    window.removeEventListener('mouseup', this.#handleMouseUp);
    this.#requestUpdateTransform();
  };

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const touchCount = event.touches.length;
    const isZoomed = this.#scale > MIN_ZOOM + 0.01;

    if (touchCount === 2) {
      preventDefault(event);
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;

      this.#startZoomGestureFromTouches(touch1, touch2);
      return;
    }

    if (touchCount !== 1) return;

    const touch = event.touches[0];
    if (!touch) return;

    // Only capture single-finger gestures when zoomed; otherwise the dialog scrolls.
    if (isZoomed) {
      preventDefault(event);
    }

    const currentTime = performance.now();
    const timeSinceLastTap = currentTime - this.#lastTapTime;

    if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
      this.#storeTapInfo(currentTime, touch);
      if (isZoomed) this.#startDragGestureFromTouch(touch);
      return;
    }

    if (this.#lastTapPosition) {
      const distance = getDistance(touch, this.#lastTapPosition);

      if (distance < DOUBLE_TAP_DISTANCE) {
        preventDefault(event);
        this.#handleZoomToggleAtPoint(touch.clientX, touch.clientY);
        this.#lastTapTime = 0;
        this.#lastTapPosition = null;
        return;
      }
    }

    this.#storeTapInfo(currentTime, touch);
    if (isZoomed) this.#startDragGestureFromTouch(touch);
  };

  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
  }

  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * Toggle zoom at a pointer position (touch double-tap or desktop double-click).
   * @param {number} clientX
   * @param {number} clientY
   */
  #handleZoomToggleAtPoint(clientX, clientY) {
    const containerCenter = {
      x: this.clientWidth / 2,
      y: this.clientHeight / 2,
    };

    let targetZoom;

    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM;
      this.#hasManualZoom = false;
      this.#translate = { x: 0, y: 0 };
    } else {
      const tolerance = 0.05;

      if (Math.abs(this.#scale - MIN_ZOOM) < tolerance) {
        targetZoom = DEFAULT_ZOOM;
      } else {
        targetZoom = MIN_ZOOM;
      }
    }

    if (targetZoom !== MIN_ZOOM) {
      const oldScale = this.#scale;
      this.#scale = Math.min(MAX_ZOOM, targetZoom);

      const distanceFromCenter = {
        x: clientX - containerCenter.x,
        y: clientY - containerCenter.y,
      };

      const scaleDelta = this.#scale / oldScale - 1.0;
      this.#translate.x -= (distanceFromCenter.x * scaleDelta) / this.#scale;
      this.#translate.y -= (distanceFromCenter.y * scaleDelta) / this.#scale;
    } else {
      this.#scale = targetZoom;
      this.#translate = { x: 0, y: 0 };
    }

    this.#requestUpdateTransform();
  }

  /**
   * @param {number} oldScale
   * @param {number} clientX
   * @param {number} clientY
   */
  #adjustTranslationForZoomAtPoint(oldScale, clientX, clientY) {
    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;
    const distanceFromCenterX = clientX - containerCenterX;
    const distanceFromCenterY = clientY - containerCenterY;
    const scaleDelta = this.#scale / oldScale - 1.0;

    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
      return;
    }

    if (touchCount === 1 && this.#isDragging && this.#scale > MIN_ZOOM + 0.01) {
      preventDefault(event);
      const touch = event.touches[0];
      if (touch) {
        this.#processDragGesture(touch);
      }
    }
  };

  #processZoomGesture(touch1, touch2) {
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;

    const currentDistance = getDistance(touch1, touch2);
    const oldScale = this.#scale;

    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, MAX_ZOOM);
    this.#hasManualZoom = true;

    const containerCenterX = this.clientWidth / 2;
    const containerCenterY = this.clientHeight / 2;
    const distanceFromCenterX = midX - containerCenterX;
    const distanceFromCenterY = midY - containerCenterY;
    const scaleDelta = this.#scale / oldScale - 1.0;

    this.#translate.x -= (distanceFromCenterX * scaleDelta) / this.#scale;
    this.#translate.y -= (distanceFromCenterY * scaleDelta) / this.#scale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * @param {Point | Touch | MouseEvent} pointer
   */
  #processDragGesture(pointer) {
    const distance = getDistance(pointer, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    const clientX = /** @type {Touch} */ (pointer).clientX ?? /** @type {Point} */ (pointer).x;
    const clientY = /** @type {Touch} */ (pointer).clientY ?? /** @type {Point} */ (pointer).y;

    const dx = clientX - this.#startPosition.x;
    const dy = clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#requestUpdateTransform();
      this.#hasDraggedBeyondThreshold = false;
    }
  };

  #constrainTranslation() {
    const containerWidth = this.clientWidth;
    const containerHeight = this.clientHeight;
    if (!containerWidth || !containerHeight || !this.#image) return;

    this.#scale = clamp(this.#scale, MIN_ZOOM, MAX_ZOOM);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      this.#syncTransformProperties();
      this.#updateZoomedAttribute();
      return;
    }

    const wrapperRect = this.getBoundingClientRect();
    const imageElement = this.#image;
    let naturalWidth, naturalHeight;

    if (imageElement.naturalWidth > 0 && imageElement.naturalHeight > 0) {
      naturalWidth = imageElement.naturalWidth;
      naturalHeight = imageElement.naturalHeight;
    } else {
      naturalWidth = wrapperRect.width;
      naturalHeight = wrapperRect.width;
    }

    const imageAspectRatio = naturalWidth / naturalHeight;
    const wrapperAspectRatio = wrapperRect.width / wrapperRect.height;

    let actualImageWidth, actualImageHeight;

    if (imageAspectRatio > wrapperAspectRatio) {
      actualImageWidth = wrapperRect.width;
      actualImageHeight = actualImageWidth / imageAspectRatio;
    } else {
      actualImageHeight = wrapperRect.height;
      actualImageWidth = actualImageHeight * imageAspectRatio;
    }

    const scaledImageWidth = actualImageWidth * this.#scale;
    const scaledImageHeight = actualImageHeight * this.#scale;

    const horizontalOverflow = Math.max(0, scaledImageWidth - wrapperRect.width);
    const verticalOverflow = Math.max(0, scaledImageHeight - wrapperRect.height);

    const maxTranslateX = horizontalOverflow / 2 / this.#scale;
    const maxTranslateY = verticalOverflow / 2 / this.#scale;

    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    this.#syncTransformProperties();
    this.#updateZoomedAttribute();
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
    this.#syncTransformProperties();
    this.#updateZoomedAttribute();
  };

  #syncTransformProperties() {
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
  }

  #updateZoomedAttribute() {
    const tolerance = 0.05;
    if (this.#scale > MIN_ZOOM + tolerance) {
      this.setAttribute('data-zoomed', '');
    } else {
      this.removeAttribute('data-zoomed');
    }
  }

  /**
   * @param {number} scale
   * @param {number} translateX
   * @param {number} translateY
   */
  #applyZoomState(scale, translateX, translateY) {
    this.#scale = scale;
    this.#startScale = scale;
    this.#translate.x = translateX;
    this.#translate.y = translateY;
    this.#syncTransformProperties();
    this.#updateZoomedAttribute();
  }

  #resetGestureState() {
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

function getDistance(point1, point2) {
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */
